/**
 * IndexedDB store for the model-property data shown in the viewer.
 *
 * Two object stores so the static AWS data (model tree) persists across viewer sessions while the dynamic
 * Mongo data (custom metadata, quantities, asset data) is dropped on viewer close / before next fetch.
 *
 *   modelPropertiesStaticData   keyPath ["fileVersionId", "nodeId"], one row per node, NO indexes — the
 *     compound primary key already orders a file's rows contiguously, so per-node lookup is
 *     `store.get([fvId, nodeId])` and file-scoped reads/deletes use a primary-key range
 *     (`staticFileRange`). Dropping the old `fileVersionId` index halves per-row bulk-insert cost.
 *   modelPropertiesDynamicData  keyPath autoIncrement (one row per Mongo doc), indexes fileVersionId and
 *     byNode ([fileVersionId, nodeId]).
 *
 * No in-memory cache — every read goes through IndexedDB, and the merged view is computed at read time by
 * `aggregateDynamicDocs` + `mergeNode`.
 */

import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_WARNING, CAD_ENGINE } from "@Services/constants";
import { hasIndexedDbSpace } from "@Services/utils/indexedDbStorage";
import { updateUnits, runWithConcurrency, ObjectTreeFieldsMinValuesMapper } from "@Services/utils/helpers";
import { resolveFileVersionIds } from "@Services/utils/resolveFileVersionIds";
import { store } from "@Store";
import { t } from "@i18n";
import { DOCUMENT_TYPE } from "@Helpers/index";
import type { PortalUnits } from "@Actions/Viewer";

const DB_NAME = "modelPropertiesData";
const STATIC_STORE = "modelPropertiesStaticData";
const DYNAMIC_STORE = "modelPropertiesDynamicData";
const LEGACY_STORE = "modelPropertiesData";
const DB_VERSION = 5;

// Rows per `getAll` when reading projected static data. Bigger = fewer IDB
// round-trips (the dominant cost for large trees) but a larger transient
// working set — up to this many freshly-deserialized rows (incl. their heavy
// `properties` blobs) are alive per in-flight file until they're projected and
// GC'd. 5k keeps each batch's synchronous projection well under a long-task
// while cutting round-trips ~5000x vs. a one-row-at-a-time cursor.
const PROJECTED_STATIC_BATCH_SIZE = 5000;

export type StaticRow = {
  fileVersionId: string;
  nodeId: number | string;
  parent?: number | string | null;
  fileId?: string;
  cubeUniqueId?: string;
  children?: Array<number | string>;
  properties?: Record<string, unknown>;
  typeProperties?: Record<string, unknown> | null;
  [key: string]: unknown;
};

export type DynamicRow = {
  fileVersionId: string;
  nodeId: number | string;
  DOCUMENT_TYPE?: string;
  // Heterogeneous Mongo payload — a quantity object, a custom-properties object,
  // or an asset-id array depending on DOCUMENT_TYPE. Narrowed at each use site.
  data?: unknown;
  [key: string]: unknown;
};

export type MergedNode = {
  nodeId?: number | string;
  fileVersionId?: string;
  parent?: number | string | null;
  properties?: Record<string, unknown>;
  customProperties?: Record<string, unknown>;
  assetData?: unknown;
  [key: string]: unknown;
};
type FileNodes = Record<string, MergedNode>;
type AllData = Record<string, FileNodes>;

/**
 * Forward minify map: original string -> small integer id (1-based). Produced by
 * `getModelPropertiesProjectedStaticData` when `convertToMinifiedData` is on.
 * fileId and fileVersionId share a single id space (`fileIdAndVersionIdMinMapper`),
 * "CUBE Name" gets its own (`cubeNameMinMapper`). Consumers build the inverse
 * (id -> string) to rehydrate nodes — see `getNodeData` in the ModelTree.
 */
export type FieldValueMinMapper = Record<string, number>;

/** Return shape of `getModelPropertiesProjectedStaticData`. */
export type ProjectedStaticData = {
  /**
   * `{ [fileVersionKey]: { [nodeId]: projectedNode } }`. When minified, the file
   * key is the fileVersionId's integer id (from `fileIdAndVersionIdMinMapper`)
   * and each node's fields are keyed by `ObjectTreeFieldsMinValuesMapper` codes
   * with fileId/fileVersionId/"CUBE Name" values stored as mapper ids. When not
   * minified, keys are the raw fileVersionId and full field names.
   */
  data: AllData;
  /** original fileId / fileVersionId -> integer id (empty when not minified). */
  fileIdAndVersionIdMinMapper: FieldValueMinMapper;
  /** original "CUBE Name" -> integer id (empty when not minified). */
  cubeNameMinMapper: FieldValueMinMapper;
};

export type MergeOptions = {
  /** When supplied, properties + typeProperties are run through `updateUnits`. */
  unitsNew?: Record<string, unknown> | undefined;
  portalUnits?: PortalUnits | null | undefined;
  /**
   * Engine type (e.g. "CAD", "BIM"). Used to handle the legacy CAD path where
   * AWS returns no model tree and the merged shape comes from flattening
   * `data` into each Mongo doc — see `mergeNode`.
   */
  engineType?: string | undefined;
  originalFileVersionId?: string | undefined;
};

let dbInstance: IDBDatabase | null = null;
let openPromise: Promise<IDBDatabase> | null = null;

// ----------------------------------------------------------------------------
// DB lifecycle
// ----------------------------------------------------------------------------

/** Drop the cached handle so the next `openDB()` reconnects instead of reusing a corpse. */
function forget(db: IDBDatabase): void {
  if (dbInstance === db) dbInstance = null;
}

/**
 * True while `db` can still start transactions. The browser retires connections on
 * its own — another tab's versionchange, storage eviction, a backing-store failure
 * (every DB of an origin shares one backing store, so a fatal write error here takes
 * the others down too) — and exposes no flag to read. The only way to ask is to start
 * a throwaway transaction: it throws InvalidStateError once the connection is closing.
 * An unused transaction auto-commits without touching disk, so the probe is free next
 * to the real read it guards.
 */
function isUsable(db: IDBDatabase): boolean {
  try {
    db.transaction(STATIC_STORE, "readonly");
    return true;
  } catch {
    return false;
  }
}

function openDB(): Promise<IDBDatabase> {
  if (dbInstance && isUsable(dbInstance)) return Promise.resolve(dbInstance);
  dbInstance = null;
  // Dedupe concurrent opens: several readers race here on a viewer load, and each
  // would otherwise start its own connection, leaking every one but the last.
  openPromise ??= new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => {
      openPromise = null;
      reject(request.error);
    };
    request.onsuccess = () => {
      const db = request.result;
      // Step aside so another tab can upgrade, and drop the handle either way:
      // `close` fires only on a forced close, never after an explicit close().
      db.onversionchange = () => {
        db.close();
        forget(db);
      };
      db.onclose = () => forget(db);
      dbInstance = db;
      openPromise = null;
      resolve(db);
    };
    request.onupgradeneeded = () => {
      const db = request.result;
      const upgradeTx = request.transaction;

      // v1 stored merged data in a single store keyed by fileVersionId. We
      // can't reliably split that into static/dynamic, so drop it. Next fetch
      // re-populates both stores from the source-of-truth services.
      if (db.objectStoreNames.contains(LEGACY_STORE)) {
        db.deleteObjectStore(LEGACY_STORE);
      }

      if (!db.objectStoreNames.contains(STATIC_STORE)) {
        // No secondary index: the compound primary key `[fileVersionId, nodeId]`
        // groups a file's rows contiguously, so file-scoped reads/deletes use a
        // primary-key range (`staticFileRange`) instead of a `fileVersionId`
        // index. Skipping the index halves per-row write cost on bulk inserts.
        db.createObjectStore(STATIC_STORE, { keyPath: ["fileVersionId", "nodeId"] });
      }

      let dynamicStore: IDBObjectStore;
      if (!db.objectStoreNames.contains(DYNAMIC_STORE)) {
        dynamicStore = db.createObjectStore(DYNAMIC_STORE, { autoIncrement: true });
        dynamicStore.createIndex("fileVersionId", "fileVersionId", { unique: false });
      } else {
        dynamicStore = upgradeTx!.objectStore(DYNAMIC_STORE);
      }
      if (!dynamicStore.indexNames.contains("byNode")) {
        dynamicStore.createIndex("byNode", ["fileVersionId", "nodeId"], { unique: false });
      }
    };
  });
  return openPromise;
}

// ----------------------------------------------------------------------------
// Storage-quota guard
// ----------------------------------------------------------------------------

export async function ensureIndexedDbSpace(payload: unknown): Promise<boolean> {
  // Cheap byte estimate, avoiding a full serialization of large payloads:
  //  - number: caller already computed a rough size — use it as-is.
  //  - non-empty array: size one sampled row and scale by length, instead of
  //    stringifying every row (the per-row size is measured with the same
  //    Blob/JSON.stringify path used below, just on a single element).
  //  - anything else: fall back to a full estimate.
  let requiredBytes: number;
  if (typeof payload === "number") {
    requiredBytes = payload;
  } else if (Array.isArray(payload) && payload.length > 0) {
    requiredBytes = JSON.stringify(payload[0]).length * payload.length * 2;
  } else {
    requiredBytes = JSON.stringify(payload).length * 2;
  }

  const hasSpace = await hasIndexedDbSpace(requiredBytes);
  if (!hasSpace) {
    store.dispatch(
      showToastNotification({
        content: t("Insufficient storage. Open Settings > Clear Cache to free space."),
        type: TOAST_TYPE_WARNING,
      }),
    );
    return false;
  }
  return true;
}

// ----------------------------------------------------------------------------
// Merge logic (mirrors `aggregateProps` + `mergeQuantityIntoProps`)
// ----------------------------------------------------------------------------

/**
 * `DynamicRow.data` is a heterogeneous Mongo payload typed `unknown`. This is the single
 * place that decides whether it can be treated as a key/value bag; arrays pass too, which
 * matches what spreading them used to do.
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function aggregateDynamicDocs(docs: DynamicRow[]) {
  let quantityData: Record<string, unknown> = {};
  let customPropertiesData: Record<string, unknown> = {};
  // Really an asset-id array, not a record — the seed is `[]` and the only writer below assigns
  // the DOCUMENT_TYPE_ASSETDATA doc's array payload. The old `Record<string, any>` was wrong.
  let assetData: unknown = [];

  for (const doc of docs) {
    if (!doc.data) continue;
    if (doc.DOCUMENT_TYPE === DOCUMENT_TYPE.DOCUMENT_TYPE_QUANTITY) {
      if (isRecord(doc.data)) quantityData = { ...quantityData, ...doc.data };
    } else if (
      doc.DOCUMENT_TYPE === DOCUMENT_TYPE.DOCUMENT_TYPE_CUSTOM_PROPERTIES ||
      doc.DOCUMENT_TYPE === DOCUMENT_TYPE.DOCUMENT_TYPE_DRAWING_PROPERTIES
    ) {
      if (isRecord(doc.data)) customPropertiesData = doc.data;
    } else if (doc.DOCUMENT_TYPE === DOCUMENT_TYPE.DOCUMENT_TYPE_ASSETDATA) {
      assetData = doc.data;
    }
  }
  return { quantityData, customPropertiesData, assetData };
}

/** Matches the legacy `mergeQuantityIntoProps` from `mergeNodesIntoResponse`. */
function mergeQuantityIntoProps(nodeProps: Record<string, unknown>, quantityData: Record<string, unknown>) {
  // No quantity for this node (the common case): skip the spread and return the
  // existing props as-is instead of allocating a copy per node. The caller
  // passes a freshly-read, unshared IDB row, and the only downstream consumer
  // (`updateUnits`) is non-mutating, so reusing the reference is safe.
  if (Object.keys(quantityData).length === 0) return nodeProps;
  return {
    ...nodeProps,
    ...quantityData,
    Quantity: quantityData.Quantity ?? nodeProps.Quantity,
    Unit: quantityData.Unit ?? nodeProps.Unit,
  };
}

/** Matches the legacy filter from `mergeNodesIntoResponse`:
 *  AWS nodes are only kept when they're the root (nodeId 0) or have a parent. */
function passesAwsFilter(row: StaticRow): boolean {
  return row.nodeId === 0 || row.parent !== null;
}

/**
 * Builds the merged node from one static row + its dynamic docs. Mirrors `mergeNodesIntoResponse` in
 * `actions/Viewer.ts`:
 *   1. Drops AWS rows that fail the parent/root filter.
 *   2. Aggregates dynamic docs (quantity merged across docs; custom & asset data are last-wins per
 *      DOCUMENT_TYPE).
 *   3. Merges quantity fields into the AWS properties via `mergeQuantityIntoProps`.
 *   4. When `unitsNew` is supplied, runs both `properties` and `typeProperties` through `updateUnits`.
 *   5. Spreads the AWS node first (or, if absent, the dynamic-doc array — the same `awsNode ?? props`
 *      quirk as the legacy path), then overrides `properties`, `customProperties`/drawing data and
 *      `assetData`.
 */
function mergeNode(
  staticRow: StaticRow | undefined,
  dynamicRows: DynamicRow[],
  options?: MergeOptions,
): MergedNode | undefined {
  // 1. AWS filter — drop rows that aren't the root and have parent === null.
  const effectiveStatic = staticRow && passesAwsFilter(staticRow) ? staticRow : undefined;
  if (!effectiveStatic && dynamicRows.length === 0) return undefined;

  // 1a. CAD with no AWS tree — flatten each Mongo doc as `{ ...rest, ...data }`
  // and accumulate. Mirrors the legacy
  //   responseData[fvId] = mongoData.data.reduce((acc, { data, ...rest }) =>
  //     ((acc[rest.nodeId] = { ...rest, ...data }), acc), {})
  // branch in `processOneFileVersion`.
  if (!effectiveStatic && options?.engineType === CAD_ENGINE) {
    let cadNode: Record<string, unknown> = {};
    for (const doc of dynamicRows) {
      const { data, ...rest } = doc;
      cadNode = { ...cadNode, ...rest, ...(isRecord(data) ? data : {}) };
    }
    return cadNode;
  }

  // 2. Dynamic aggregation.
  const { quantityData, customPropertiesData, assetData } = aggregateDynamicDocs(dynamicRows);

  // 3. Quantity merged into AWS properties.
  const propertiesWithCubeData = mergeQuantityIntoProps(effectiveStatic?.properties ?? {}, quantityData);

  // 4. Optional unit conversion (matches the legacy `if (unitsNew && Object.keys(unitsNew).length)` branch).
  const unitsNew = options?.unitsNew;

  if (effectiveStatic) {
    if (unitsNew && Object.keys(unitsNew).length > 0) {
      effectiveStatic.properties = updateUnits(propertiesWithCubeData, unitsNew, options.portalUnits);
      effectiveStatic.typeProperties = updateUnits(effectiveStatic.typeProperties ?? {}, unitsNew, options.portalUnits);
    } else {
      effectiveStatic.properties = propertiesWithCubeData;
    }
  }

  // 5. Final shape — same field order as `mergeNodesIntoResponse`.
  return {
    ...(effectiveStatic ?? dynamicRows),
    properties: effectiveStatic?.properties ?? {},
    ...customPropertiesData,
    assetData,
  };
}

function groupDynamicByNode(rows: DynamicRow[]): Record<string, DynamicRow[]> {
  const map: Record<string, DynamicRow[]> = {};
  for (const row of rows) {
    const key = String(row.nodeId);
    (map[key] ??= []).push(row);
  }
  return map;
}

// ----------------------------------------------------------------------------
// Static (AWS) writes
// ----------------------------------------------------------------------------

/**
 * Primary-key range covering every row for one file in the static store, whose
 * key is `[fileVersionId, nodeId]`. An empty array `[]` sorts after any number
 * or string in IndexedDB's key ordering, so `[fileVersionId, []]` is greater
 * than `[fileVersionId, <anyNodeId>]` — the range matches all nodes under the
 * file. Replaces the dropped `fileVersionId` index for file-scoped operations.
 */
function staticFileRange(fileVersionId: string): IDBKeyRange {
  return IDBKeyRange.bound([fileVersionId], [fileVersionId, []]);
}

/** Replaces all static rows for `fileVersionId` with the supplied rows. */
export async function setStaticDataForFile(fileVersionId: string, rows: StaticRow[], size: number): Promise<void> {
  if (!(await ensureIndexedDbSpace(size))) return;
  const db = await openDB();
  return new Promise((resolve, reject) => {
    // "relaxed" durability: don't force a disk fsync before `oncomplete` fires.
    // The commit (the slow part of a bulk insert) reports done as soon as the
    // data is buffered, not after it's physically flushed. Safe here because a
    // rare lost flush just re-triggers the refetch in `processOneFileVersion`.
    const tx = db.transaction(STATIC_STORE, "readwrite", { durability: "relaxed" });
    const objectStore = tx.objectStore(STATIC_STORE);

    // Clear this file's existing rows with one range delete (queued before the
    // puts, so it runs first), then insert the fresh rows. No getAllKeys round
    // trip and no per-key delete loop.
    objectStore.delete(staticFileRange(fileVersionId));
    for (const row of rows) objectStore.put(row);

    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(new Error("IndexedDB transaction aborted", { cause: tx.error }));
  });
}

// ----------------------------------------------------------------------------
// Dynamic (Mongo) writes
// ----------------------------------------------------------------------------

/** Replaces all dynamic rows for `fileVersionId` with the supplied rows. */
export async function setDynamicDataForFile(fileVersionId: string, rows: DynamicRow[]): Promise<void> {
  if (!(await ensureIndexedDbSpace(rows))) return;
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DYNAMIC_STORE, "readwrite");
    const objectStore = tx.objectStore(DYNAMIC_STORE);

    const keysReq = objectStore.index("fileVersionId").getAllKeys(fileVersionId);
    keysReq.onsuccess = () => {
      for (const k of keysReq.result) objectStore.delete(k);
      for (const row of rows) objectStore.add(row);
    };

    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(new Error("IndexedDB transaction aborted", { cause: tx.error }));
  });
}

// ----------------------------------------------------------------------------
// Reads / existence checks
// ----------------------------------------------------------------------------

/** The two members every row in both stores is keyed on. */
function isNodeId(value: unknown): value is number | string {
  return typeof value === "number" || typeof value === "string";
}

/**
 * `IDBObjectStore.get`/`getAll` and `IDBIndex.getAll` are declared `IDBRequest<any>` / `IDBRequest<any[]>`
 * in lib.dom — IndexedDB cannot know what a store holds — so every read below takes its request as
 * `unknown` and narrows the result through these guards instead of asserting it. Both stores are private
 * to this module (`DB_NAME` is opened nowhere else) and every row is written by the write functions above,
 * which take `StaticRow`/`DynamicRow`; IndexedDB additionally refuses a row carrying no value at the static
 * store's keyPath, so the keyed pair is what is checked here.
 */
function isStaticRow(row: unknown): row is StaticRow {
  return isRecord(row) && typeof row.fileVersionId === "string" && isNodeId(row.nodeId);
}

function isDynamicRow(row: unknown): row is DynamicRow {
  return isRecord(row) && typeof row.fileVersionId === "string" && isNodeId(row.nodeId);
}

/** A `getAll` result. The array is handed back untouched when every row checks out, so a batched
 *  read keeps the row count its pagination keys off. */
function asStaticRows(rows: unknown[]): StaticRow[] {
  if (rows.every(isStaticRow)) return rows;
  return rows.filter(isStaticRow);
}

function asDynamicRows(rows: unknown[]): DynamicRow[] {
  if (rows.every(isDynamicRow)) return rows;
  return rows.filter(isDynamicRow);
}

export async function getStaticDataForFile(fileVersionId: string): Promise<StaticRow[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STATIC_STORE, "readonly");
    const req: IDBRequest<unknown[]> = tx.objectStore(STATIC_STORE).getAll(staticFileRange(fileVersionId));
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(asStaticRows(req.result));
  });
}

export async function getDynamicDataForFile(fileVersionId: string): Promise<DynamicRow[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DYNAMIC_STORE, "readonly");
    const req: IDBRequest<unknown[]> = tx.objectStore(DYNAMIC_STORE).index("fileVersionId").getAll(fileVersionId);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(asDynamicRows(req.result));
  });
}

/** Returns all dynamic rows for a single (fileVersionId, nodeId). */
export async function getDynamicDocsForNode(fileVersionId: string, nodeId: number | string): Promise<DynamicRow[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DYNAMIC_STORE, "readonly");
    const req: IDBRequest<unknown[]> = tx.objectStore(DYNAMIC_STORE).index("byNode").getAll([fileVersionId, nodeId]);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(asDynamicRows(req.result));
  });
}

/**
 * Replaces every dynamic row for a single (fileVersionId, nodeId) with the
 * supplied list. Used by per-node mutations (custom metadata, quantity,
 * asset data) — they read the node's docs, modify them, then call this to
 * commit.
 */
export async function replaceDynamicDocsForNode(
  fileVersionId: string,
  nodeId: number | string,
  docs: DynamicRow[],
): Promise<void> {
  if (!(await ensureIndexedDbSpace(docs))) return;
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DYNAMIC_STORE, "readwrite");
    const objectStore = tx.objectStore(DYNAMIC_STORE);
    const keysReq = objectStore.index("byNode").getAllKeys([fileVersionId, nodeId]);
    keysReq.onsuccess = () => {
      for (const k of keysReq.result) objectStore.delete(k);
      for (const doc of docs) objectStore.add(doc);
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(new Error("IndexedDB transaction aborted", { cause: tx.error }));
  });
}

/**
 * Batched read: each node's dynamic docs, index-aligned with `keys`, fetched in
 * ONE readonly transaction (vs one transaction per node). Used by the bulk
 * mutation thunks so a multi-node edit does a single read round-trip.
 */
export function getDynamicDocsForNodes(
  keys: Array<{ fileVersionId: string; nodeId: number | string }>,
): Promise<DynamicRow[][]> {
  if (keys.length === 0) return Promise.resolve([]);
  return openDB().then(
    (db) =>
      new Promise<DynamicRow[][]>((resolve, reject) => {
        const tx = db.transaction(DYNAMIC_STORE, "readonly");
        const idx = tx.objectStore(DYNAMIC_STORE).index("byNode");
        const rows: DynamicRow[][] = new Array<DynamicRow[]>(keys.length);
        keys.forEach(({ fileVersionId, nodeId }, i) => {
          const req: IDBRequest<unknown[]> = idx.getAll([fileVersionId, nodeId]);
          req.onsuccess = () => {
            rows[i] = asDynamicRows(req.result);
          };
        });
        tx.oncomplete = () => resolve(rows);
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(new Error("IndexedDB transaction aborted", { cause: tx.error }));
      }),
  );
}

/**
 * Batched write: replaces the dynamic docs for several (fileVersionId, nodeId)
 * pairs in ONE readwrite transaction (vs one transaction per node). Each entry's
 * existing rows are deleted and its new `docs` added.
 */
export async function replaceDynamicDocsForNodes(
  entries: Array<{ fileVersionId: string; nodeId: number | string; docs: DynamicRow[] }>,
): Promise<void> {
  if (entries.length === 0) return;
  if (!(await ensureIndexedDbSpace(entries.flatMap((e) => e.docs)))) return;
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DYNAMIC_STORE, "readwrite");
    const objectStore = tx.objectStore(DYNAMIC_STORE);
    const idx = objectStore.index("byNode");
    for (const { fileVersionId, nodeId, docs } of entries) {
      const keysReq = idx.getAllKeys([fileVersionId, nodeId]);
      keysReq.onsuccess = () => {
        for (const k of keysReq.result) objectStore.delete(k);
        for (const doc of docs) objectStore.add(doc);
      };
    }
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(new Error("IndexedDB transaction aborted", { cause: tx.error }));
  });
}

/** True if any static rows exist for this file in IDB. Stops at the first match. */
export async function hasStaticDataForFile(fileVersionId: string): Promise<boolean> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STATIC_STORE, "readonly");
    const req = tx.objectStore(STATIC_STORE).openKeyCursor(staticFileRange(fileVersionId));
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result !== null);
  });
}

/** True if any dynamic rows exist for this file in IDB. Stops at the first match. */
export async function hasDynamicDataForFile(fileVersionId: string): Promise<boolean> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(DYNAMIC_STORE, "readonly");
    const req = tx.objectStore(DYNAMIC_STORE).index("fileVersionId").openKeyCursor(IDBKeyRange.only(fileVersionId));
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result !== null);
  });
}

/**
 * True when EITHER store has at least one row for the file. Existence-only
 * (key-cursor) — loads no row values. Used by `isFileModelDataWiped` to detect
 * a cache wipe by another tab.
 */
export async function hasFileModelData(fileVersionId: string): Promise<boolean> {
  const [hasStatic, hasDynamic] = await Promise.all([
    hasStaticDataForFile(fileVersionId),
    hasDynamicDataForFile(fileVersionId),
  ]);
  return hasStatic || hasDynamic;
}

// Per-tab, in-memory snapshot of which files actually had rows in IDB at the
// moment their load completed (ids only — no model data, no copies). Lets
// `isFileModelDataWiped` tell an EXTERNAL wipe (was present, now gone) apart
// from a file that legitimately has no model data — e.g. a CAD file with no AWS
// tree and no Mongo docs, where `hasFileModelData` is `false` even normally.
const filesPresentAtLoad = new Set<string>();

/** Record whether `fileVersionId` had rows in IDB when its load finished. */
export function markFileLoadState(fileVersionId: string, present: boolean): void {
  if (present) filesPresentAtLoad.add(fileVersionId);
  else filesPresentAtLoad.delete(fileVersionId);
}
export function clearFilesLoadState(): void {
  filesPresentAtLoad.clear();
}

/**
 * True when the file had data at load time (per {@link markFileLoadState}) but
 * its IDB rows are now gone — i.e. another tab's Settings > Clear Cache wiped
 * the shared, per-origin store underneath this tab. Files that never had data
 * (legitimately-empty CAD) return `false`. Existence-only; makes no data copies.
 */
export async function isFileModelDataWiped(fileVersionId: string): Promise<boolean> {
  if (!filesPresentAtLoad.has(fileVersionId)) return false;
  return !(await hasFileModelData(fileVersionId));
}

/**
 * Tab-level wipe check for a viewer file. Resolves a merged file to its
 * constituents (via `resolveFileVersionIds`) and reports a wipe when ANY
 * constituent that had data at load is now gone (see {@link isFileModelDataWiped}).
 * Files that never had data (e.g. legitimately-empty CAD) return `false`.
 * Existence-only; makes no data copies. Callers gate on load state — the hook
 * skips this while `isLoading` / before `isLoaded`.
 */
/** `fileVersionId` is absent when no file is open in the viewer — nothing is loaded, so nothing is wiped. */
export async function isModelPropertiesWiped(fileVersionId: string | undefined): Promise<boolean> {
  const ids = resolveFileVersionIds(store.getState().filesData, fileVersionId);
  if (ids.length === 0) return false;
  // A merged file is wiped if ANY constituent that had data is now gone.
  const results = await Promise.all(ids.map((id) => isFileModelDataWiped(id)));
  return results.some((wiped) => wiped);
}

/**
 * Reads static + dynamic for the file and returns the merged view.
 * `options` is optional — when omitted (or partially supplied), missing
 * fields are resolved from the Redux store via `resolveMergeOptions`.
 * This is the single resolution point — `getModelPropertiesData` and any
 * other caller that goes through here inherits the same behavior.
 */
async function getMergedDataForFile(fileVersionId: string, options?: MergeOptions): Promise<FileNodes> {
  const effectiveOptions = resolveMergeOptions(options);
  const [staticRows, dynamicRows] = await Promise.all([
    getStaticDataForFile(fileVersionId),
    getDynamicDataForFile(fileVersionId),
  ]);

  const dynamicByNode = groupDynamicByNode(dynamicRows);
  const merged: FileNodes = {};

  for (const row of staticRows) {
    const key = String(row.nodeId);
    const m = mergeNode(row, dynamicByNode[key] ?? [], effectiveOptions);
    if (m) merged[key] = m;
    delete dynamicByNode[key];
  }
  // Nodes that have dynamic data but no matching static row.
  for (const [key, docs] of Object.entries(dynamicByNode)) {
    const m = mergeNode(undefined, docs, effectiveOptions);
    if (m) merged[key] = m;
  }
  return merged;
}

/**
 * Builds a complete `MergeOptions` by combining a partial caller-supplied override with values pulled from
 * the Redux store:
 *   - `unitsNew`    ← `state.filesData[fileVersionId].Units`
 *   - `engineType`  ← `state.filesData[fileVersionId].engineType`
 *   - `portalUnits` ← `state.portalUnits`
 * Explicit (non-`undefined`) fields in `override` always win, so direct callers of
 * `getMergedDataForNode` / `getModelPropertiesData` / `getNodeModelPropertiesData` can omit options and
 * still get the hook's merged-view behaviour.
 */
function resolveMergeOptions(options?: MergeOptions): MergeOptions {
  const fileId = options?.originalFileVersionId;
  if (!fileId) return options ?? {};

  const state = store.getState();
  const fileEntry = state.filesData[fileId];

  return {
    unitsNew: options.unitsNew ?? fileEntry?.Units,
    portalUnits: options.portalUnits ?? state.portalUnits,
    engineType: options.engineType ?? fileEntry?.engineType,
  };
}

/**
 * Reads the merged view for one or more nodes within a single file. `nodeId` can be a single id or an
 * array. Always returns `Record<nodeId, MergedNode>`; nodes with no data in either store are omitted.
 * `options` is optional — defaults are resolved from the Redux store via `resolveMergeOptions`. All reads
 * happen in a single IDB transaction.
 */
/**
 * Per-node reads issue two IDB requests per node (static `get` + dynamic `getAll`) inside one transaction.
 * To stop a very large selection (e.g. select-all) from queuing thousands of requests and retaining every
 * row at once, node lists longer than this are read in sequential chunks of this size.
 */
const MERGED_NODE_READ_CHUNK = 250;

export async function getMergedDataForNode(
  fileVersionId: string,
  nodeId: (number | string) | Array<number | string>,
  options?: MergeOptions,
): Promise<Record<string, MergedNode>> {
  const nodeIds = Array.isArray(nodeId) ? nodeId : [nodeId];
  if (nodeIds.length === 0) return {};

  // Resolve units/portalUnits/engineType once for the whole call (not per chunk).
  const effectiveOptions = resolveMergeOptions(options);

  if (nodeIds.length <= MERGED_NODE_READ_CHUNK) {
    return readMergedDocsForNodeChunk(fileVersionId, nodeIds, effectiveOptions);
  }

  const result: Record<string, MergedNode> = {};
  for (let i = 0; i < nodeIds.length; i += MERGED_NODE_READ_CHUNK) {
    const chunk = nodeIds.slice(i, i + MERGED_NODE_READ_CHUNK);
    Object.assign(result, await readMergedDocsForNodeChunk(fileVersionId, chunk, effectiveOptions));
  }
  return result;
}

/** One read transaction: static `get` + dynamic `getAll` per node, then merge. */
function readMergedDocsForNodeChunk(
  fileVersionId: string,
  nodeIds: Array<number | string>,
  effectiveOptions: MergeOptions,
): Promise<Record<string, MergedNode>> {
  return openDB().then(
    (db) =>
      new Promise<Record<string, MergedNode>>((resolve, reject) => {
        const tx = db.transaction([STATIC_STORE, DYNAMIC_STORE], "readonly");
        const staticStore = tx.objectStore(STATIC_STORE);
        const dynamicIdx = tx.objectStore(DYNAMIC_STORE).index("byNode");

        const staticRows: Record<string, StaticRow | undefined> = {};
        const dynamicRows: Record<string, DynamicRow[]> = {};

        for (const id of nodeIds) {
          const key = String(id);
          const staticReq: IDBRequest<unknown> = staticStore.get([fileVersionId, id]);
          const dynamicReq: IDBRequest<unknown[]> = dynamicIdx.getAll([fileVersionId, id]);
          staticReq.onsuccess = () => {
            const row = staticReq.result;
            staticRows[key] = isStaticRow(row) ? row : undefined;
          };
          dynamicReq.onsuccess = () => {
            dynamicRows[key] = asDynamicRows(dynamicReq.result);
          };
        }

        tx.oncomplete = () => {
          const result: Record<string, MergedNode> = {};
          for (const id of nodeIds) {
            const key = String(id);
            const merged = mergeNode(staticRows[key], dynamicRows[key] ?? [], effectiveOptions);
            if (merged) result[key] = merged;
          }
          resolve(result);
        };
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(new Error("IndexedDB transaction aborted", { cause: tx.error }));
      }),
  );
}

/**
 * Reads merged data for one file (string) or several (string[], for merged files where the request carries
 * an array of source fileVersionIds). Result is wrapped as `{ [fvId]: ... }` so consumers keep using
 * `data[fileVersionId][nodeId]` access regardless of the input shape. Option resolution is handled inside
 * `getMergedDataForFile` per file.
 */
export async function getModelPropertiesData(
  options: Partial<MergeOptions> & { fileVersionId?: string | string[] },
): Promise<AllData> {
  const effectiveOptions = resolveMergeOptions(options);
  // Resolve which file versions to read: an explicit `fileVersionId` (an
  // already-resolved id or ids) wins; otherwise expand `originalFileVersionId`
  // — the viewer's file, where a merged file fans out to its constituent
  // versions — from the Redux store, so callers needn't resolve ids themselves.
  const ids =
    options.fileVersionId != null
      ? Array.isArray(options.fileVersionId)
        ? options.fileVersionId
        : [options.fileVersionId]
      : resolveFileVersionIds(store.getState().filesData, options.originalFileVersionId ?? "");
  // Seeded in `ids` order so the result's key order — and the entry for a file that yields
  // nothing — doesn't depend on which file finishes first.
  const all: AllData = {};
  for (const id of ids) all[id] = {};
  // Cap concurrency so only a few files' transient working sets (the full
  // `getAll` static + dynamic arrays, `dynamicByNode` map, and the spread
  // garbage from `mergeNode`) are alive at once — lowers the peak-memory /
  // CPU spike vs. fanning out every file with `Promise.all`. Each task writes its
  // own entry, so nothing depends on the order results come back in.
  await runWithConcurrency(
    ids.map((id) => async () => {
      all[id] = await getMergedDataForFile(id, effectiveOptions);
    }),
    5,
  );
  return all;
}

/**
 * Projected, STATIC-only read for one or more files. Unlike `getModelPropertiesData`, this never merges
 * dynamic (Mongo) data, never runs unit conversion, and never materializes the full per-node payload.
 *
 * IndexedDB has no native field projection — every read deserializes the whole stored row. So each file is
 * read in BATCHES via `getAll(range, BATCH_SIZE)` and only the requested fields are copied out per node;
 * the heavy `properties`/`typeProperties` blobs are dropped as soon as a batch is projected and the batch
 * array goes out of scope. Batching (vs. a one-row-at-a-time cursor, one IDB round-trip per node) turns ~N
 * per-row round-trips into ~N/BATCH_SIZE — the dominant win for large trees.
 *
 * @param fileVersionId  a single id or an array (merged files)
 * @param options.fields          top-level `StaticRow` keys to keep (e.g. ["parent", "children"]);
 *                                `nodeId` is always included.
 * @param options.propertyFields  specific keys to pull out of `row.properties` without retaining the rest
 *                                (e.g. ["CUBE Name"]). Emitted under `properties` on each projected node.
 * @returns `{ [fvId]: { [nodeId]: projectedNode } }`
 */
export async function getModelPropertiesProjectedStaticData(
  fileVersionId: string | string[],
  options: { fields?: string[]; propertyFields?: string[] } = {},
  convertToMinifiedData = true,
): Promise<ProjectedStaticData> {
  const ids = Array.isArray(fileVersionId) ? fileVersionId : [fileVersionId];
  const all: AllData = {};

  const fileIdAndVersionIdMinMapper: FieldValueMinMapper = {};
  const cubeNameMinMapper: FieldValueMinMapper = {};

  let fileAndVersonIdCounter = 1;
  let cubeNameCounter = 1;

  if (ids.length === 0) return { data: all, fileIdAndVersionIdMinMapper, cubeNameMinMapper };

  const fields = options.fields ?? [];
  const propertyFields = options.propertyFields ?? [];
  const db = await openDB();

  const minifyData = (fieldsData: Record<string | number, unknown>, field: string, row: Record<string, unknown>) => {
    if (convertToMinifiedData) {
      switch (field) {
        case "nodeId":
        case "parent": {
          fieldsData[ObjectTreeFieldsMinValuesMapper[field]] = row[field];
          break;
        }
        case "fileId":
        case "fileVersionId": {
          const value = String(row[field]);
          if (!Object.hasOwn(fileIdAndVersionIdMinMapper, value)) {
            fileIdAndVersionIdMinMapper[value] = fileAndVersonIdCounter;
            fileAndVersonIdCounter++;
          }

          fieldsData[ObjectTreeFieldsMinValuesMapper[field]] = fileIdAndVersionIdMinMapper[value];
          break;
        }
        case "CUBE Name": {
          const value = String(row[field]);
          if (!Object.hasOwn(cubeNameMinMapper, value)) {
            cubeNameMinMapper[value] = cubeNameCounter;
            cubeNameCounter++;
          }

          fieldsData[ObjectTreeFieldsMinValuesMapper[field]] = cubeNameMinMapper[value];
          break;
        }
        case "cubeUniqueId": {
          fieldsData[ObjectTreeFieldsMinValuesMapper[field]] = row[field];
          break;
        }

        default: {
          fieldsData[field] = row[field];
        }
      }
    } else {
      fieldsData[field] = row[field];
    }
  };

  const readOne = (fvId: string) =>
    new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STATIC_STORE, "readonly");
      const store = tx.objectStore(STATIC_STORE);
      const fileNodes: FileNodes = {};

      // Project one batch's rows into `fileNodes`, keeping only the requested
      // fields so the full rows (incl. heavy `properties` blobs) can be GC'd
      // once this batch returns.
      const projectRows = (rows: StaticRow[]) => {
        for (const row of rows) {
          // Mirror the merge path's AWS filter (passesAwsFilter): keep the root
          // (nodeId 0) and any node with a parent; skip orphan non-root rows so
          // the projected result matches what `getMergedDataForFile` would return.
          if (!passesAwsFilter(row)) continue;

          // Project only the requested fields. `nodeId` is always kept so the
          // node stays identifiable.
          const projected: MergedNode = {};
          minifyData(projected, "nodeId", row);
          for (const f of fields) {
            if (f in row) minifyData(projected, f, row);
          }
          if (propertyFields.length && row.properties) {
            const rowProps = row.properties;
            for (const pf of propertyFields) {
              if (pf in rowProps) minifyData(projected, pf, rowProps);
            }
          }

          fileNodes[String(row.nodeId)] = projected;
        }
      };

      // Walk the file's contiguous `[fvId, nodeId]` key range in
      // PROJECTED_STATIC_BATCH_SIZE chunks — one `getAll` round-trip per batch
      // instead of one per row. The next batch starts just after the last key
      // we saw (`lowerOpen`), so no row is read twice. Re-issuing `getAll` on the
      // same store synchronously inside `onsuccess` keeps the transaction alive
      // until a short (final) batch, at which point `oncomplete` fires.
      const readBatch = (lower: [string] | [string, number | string], lowerOpen: boolean) => {
        const range = IDBKeyRange.bound(lower, [fvId, []], lowerOpen, false);
        const req: IDBRequest<unknown[]> = store.getAll(range, PROJECTED_STATIC_BATCH_SIZE);
        req.onsuccess = () => {
          const rows = req.result;
          projectRows(asStaticRows(rows));
          if (rows.length === PROJECTED_STATIC_BATCH_SIZE) {
            // Pagination keys off the RAW batch length, so a row the guard rejected can never cut
            // the walk short; the last row of a full batch is keyed, so it carries the next lower
            // bound.
            const lastRow = rows[rows.length - 1];
            if (isStaticRow(lastRow)) readBatch([fvId, lastRow.nodeId], true);
          }
        };
        req.onerror = () => reject(req.error);
      };

      readBatch([fvId], false);

      tx.oncomplete = () => {
        if (convertToMinifiedData) {
          let mappedId = fileIdAndVersionIdMinMapper[fvId];
          if (mappedId === undefined) {
            mappedId = fileAndVersonIdCounter++;
            fileIdAndVersionIdMinMapper[fvId] = mappedId;
          }
          all[mappedId] = fileNodes;
        } else {
          all[fvId] = fileNodes;
        }
        resolve();
      };
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(new Error("IndexedDB transaction aborted", { cause: tx.error }));
    });

  // Cap concurrency to bound how many files' batches are in flight at once. Each
  // in-flight file transiently holds up to PROJECTED_STATIC_BATCH_SIZE
  // freshly-deserialized rows (until they're projected + GC'd) plus its slim
  // accumulator, so the peak working set is ~concurrency × batch-size rows.
  await runWithConcurrency(
    ids.map((id) => () => readOne(id)),
    5,
  );

  return { data: all, fileIdAndVersionIdMinMapper, cubeNameMinMapper };
}

/**
 * Clears every file except those in `keep` — used by Settings > Clear Cache.
 *
 * Key-only cursors (never the row payload, so no row data is loaded). The static store's compound primary
 * key `[fileVersionId, nodeId]` keeps a file's rows contiguous, so this advances ONE step per file —
 * jumping file boundaries with `cursor.continue([fileVersionId, []])` — and drops a non-kept file's whole
 * range in a single `delete(staticFileRange)`, i.e. O(files) not O(nodes). The dynamic store
 * (autoIncrement key) must delete per doc by primary key via its `fileVersionId` index, but skips a kept
 * file's entries in one jump.
 */
export async function clearOtherFilesModelPropertiesData(keepFileVersionIds: string[]): Promise<void> {
  const keep = new Set(keepFileVersionIds);
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction([STATIC_STORE, DYNAMIC_STORE], "readwrite");

    // Static store: step once per file, not per node. `[fileVersionId, []]`
    // sorts after every `[fileVersionId, nodeId]` (an array orders after any
    // number/string), so `continue([fileVersionId, []])` lands on the next
    // file's first row — skipping the rest of this file's nodes. Non-kept files
    // are removed with a single range delete instead of one delete per row.
    const staticStore = tx.objectStore(STATIC_STORE);
    const staticCursorReq = staticStore.openKeyCursor();
    staticCursorReq.onsuccess = () => {
      const cursor = staticCursorReq.result;
      if (!cursor) return;
      // Primary key is the compound `[fileVersionId, nodeId]` (see the store's keyPath).
      const fileVersionId = Array.isArray(cursor.key) ? String(cursor.key[0]) : String(cursor.key);
      if (!keep.has(fileVersionId)) {
        staticStore.delete(staticFileRange(fileVersionId));
      }
      cursor.continue([fileVersionId, []]);
    };

    // Dynamic store: autoIncrement primary key, so deletes go by primary key via
    // the `fileVersionId` index (no range delete by index). Non-kept docs are
    // walked + deleted individually; a KEPT file's entries are skipped in one
    // jump — `fileVersionId + "\u0000"` is the smallest key past all of them.
    const dynamicStore = tx.objectStore(DYNAMIC_STORE);
    const dynamicCursorReq = dynamicStore.index("fileVersionId").openKeyCursor();
    dynamicCursorReq.onsuccess = () => {
      const cursor = dynamicCursorReq.result;
      if (!cursor) return;
      // The `fileVersionId` index keys on that string field.
      const fileVersionId = String(cursor.key);
      if (keep.has(fileVersionId)) {
        cursor.continue(fileVersionId + "\u0000");
      } else {
        dynamicStore.delete(cursor.primaryKey);
        cursor.continue();
      }
    };

    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(new Error("IndexedDB transaction aborted", { cause: tx.error }));
  });
}

// ----------------------------------------------------------------------------
// Backward-compat: one shim retained — only `createAssetDataPayload` in
// actions/Viewer.ts still goes through this name. Routes to the new
// per-node merged-read API.
// ----------------------------------------------------------------------------

export async function getNodeModelPropertiesData(
  fileVersionId: string,
  nodeId: number | string,
  options?: MergeOptions,
): Promise<MergedNode | undefined> {
  const result = await getMergedDataForNode(fileVersionId, nodeId, options);
  return result[String(nodeId)];
}
